import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * GlobalEscBack — pressing Escape acts as the app-wide "back" button.
 *
 * Behaviour:
 *   - On Escape ↓ from anywhere in the app, calls `navigate(-1)`
 *     (returns the user to the previous route with state intact).
 *   - Skips when the user is typing inside an <input>, <textarea>,
 *     select or a contentEditable element — Escape there is used to
 *     blur/cancel and must not steal focus.
 *   - Skips when a modal/dialog is open (detected via
 *     `[role="dialog"]`, `[data-modal-open="true"]`, or an element
 *     with class `modal-open`) so the modal's own onKeyDown handler
 *     handles Escape (usually "close modal").
 *   - Skips on login / dashboard root pages so users don't get
 *     stuck bouncing between pre-auth routes.
 */
const ROOT_ROUTES = new Set([
  '/', '/login', '/customer-login', '/admin-login', '/admin',
  '/customer-dashboard', '/handler-dashboard', '/admin-dashboard',
]);

const GlobalEscBack = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;

      // Focused editable element? let Escape do its normal thing.
      const el = document.activeElement;
      if (el) {
        const tag = (el.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        if (el.isContentEditable) return;
      }

      // Modal open? let the modal handle Escape.
      if (
        document.querySelector('[role="dialog"]') ||
        document.querySelector('[data-modal-open="true"]') ||
        document.querySelector('.modal-open')
      ) return;

      // Root-level pages: don't navigate back further.
      if (ROOT_ROUTES.has(location.pathname)) return;

      e.preventDefault();
      navigate(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, location.pathname]);

  return null;
};

export default GlobalEscBack;
